import { useCallback } from 'react';

/**
 * Hook that returns a navigate function aware of cross-app routing.
 *
 * For paths that belong to the current SPA (matched via `ownRoutePrefixes`),
 * the provided `navigate` callback is called (client-side React Router).
 * For all other paths a full page load is triggered via `globalThis.location.href`,
 * with an optional locale prefix prepended when `locale` is not `en-us`.
 *
 * This is the same logic used by the Sidebar's `navigateTo` callback, extracted
 * here so that any component (e.g. QuickLinks) can reuse it without duplication.
 *
 * @param ownRoutePrefixes - Array of path prefixes owned by the current SPA
 *                           (e.g. APP_ROUTE_PREFIXES.partnerPortal)
 * @param locale           - Current locale string (e.g. 'en-us', 'fr-ca')
 * @param navigate         - React Router navigate function (or any client-side nav fn)
 */
export function useCrossAppNavigate(
  ownRoutePrefixes: string[],
  locale: string,
  navigate: (path: string) => void
): (path: string) => void {
  return useCallback(
    (path: string) => {
      // Strip locale prefix before prefix-matching (e.g. /de-de/parts-center → /parts-center)
      const basePath = path.replace(/^\/[a-z]{2}-[a-z]{2}/i, '') || '/';

      const isOwnRoute =
        (basePath === '/' && ownRoutePrefixes.includes('/')) ||
        ownRoutePrefixes.some((prefix) => prefix !== '/' && basePath.startsWith(prefix));

      if (!isOwnRoute) {
        const localePrefix = locale && locale !== 'en-us' ? `/${locale}` : '';
        globalThis.location.href = `${localePrefix}${path}`;
        return;
      }

      navigate(path);
    },
    [ownRoutePrefixes, locale, navigate]
  );
}
